import { useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Trash2, Users, PiggyBank, MapPin, Pencil, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminTitle } from "@/admin/AdminLayout";
import { StatusBadge, UserPhoto } from "@/components/Brand";
import { GradientStat } from "@/components/Colorful";
import { PinInput } from "@/components/PinInput";
import { useAuth } from "@/context/AuthContext";
import { mapsUrl, fmtCoord } from "@/lib/photo";
import { api, errMsg, useApi } from "@/lib/api";
import { rp, fmtDate, rank, ROLE_LABEL, fmtAcc } from "@/lib/format";
import { AccountNo } from "@/components/AccountNo";

const SavingsForm = ({ member, onDone }) => {
  const [f, setF] = useState({ type: "wajib", direction: "in", amount: 0, description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.amount <= 0) return toast.error("Masukkan nominal");
    setBusy(true);
    try {
      await api.post("/admin/savings", { ...f, user_id: member.user_id });
      toast.success("Transaksi simpanan dicatat");
      setF({ ...f, amount: 0, description: "" });
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
          <SelectTrigger data-testid="admin-savings-direction"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="in">Setoran</SelectItem><SelectItem value="out">Penarikan</SelectItem></SelectContent>
        </Select>
        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
          <SelectTrigger data-testid="admin-savings-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="pokok">Pokok</SelectItem><SelectItem value="wajib">Wajib</SelectItem><SelectItem value="sukarela">Sukarela</SelectItem></SelectContent>
        </Select>
      </div>
      <Input data-testid="admin-savings-amount" placeholder="Rp 0" value={f.amount ? `Rp ${f.amount.toLocaleString("id-ID")}` : ""} onChange={(e) => setF({ ...f, amount: Number(e.target.value.replace(/\D/g, "")) || 0 })} />
      <Input data-testid="admin-savings-desc" placeholder="Keterangan (opsional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <button onClick={submit} disabled={busy} data-testid="admin-savings-submit" className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg">Catat Transaksi</button>
    </div>
  );
};

const RoleOptions = () => {
  const { user } = useAuth();
  const roles = rank(user) >= 2 ? ["member", "staff", "admin"] : ["member"];
  return roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>);
};

const EditForm = ({ member, onDone }) => {
  const { user } = useAuth();
  const isSuper = user.role === "superadmin";
  const [f, setF] = useState({ name: member.name, email: member.email, phone: member.phone || "", nik: member.nik || "", address: member.address || "", status: member.status, role: member.role });
  const save = async () => {
    const body = { ...f };
    if (!isSuper || body.email.trim().toLowerCase() === member.email) delete body.email;
    else if (!window.confirm(`Ganti email login ${member.name} menjadi ${body.email}? Anggota akan dikeluarkan dan harus masuk dengan email baru.`)) return;
    try {
      await api.patch(`/admin/members/${member.user_id}`, body);
      toast.success("Data anggota disimpan");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <div><label className="text-xs font-semibold text-slate-600">Nama</label><Input data-testid="admin-edit-name" className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nama" /></div>
      {isSuper && (
        <div>
          <label className="text-xs font-semibold text-slate-600">Email login (Google)</label>
          <Input data-testid="admin-edit-email" type="email" className="mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="admin-edit-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="No. HP" />
        <Input data-testid="admin-edit-nik" value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} placeholder="NIK" />
      </div>
      <Textarea data-testid="admin-edit-address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Alamat" />
      <div className="grid grid-cols-2 gap-2">
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
          <SelectTrigger data-testid="admin-edit-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="aktif">Aktif</SelectItem><SelectItem value="nonaktif">Nonaktif</SelectItem></SelectContent>
        </Select>
        <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
          <SelectTrigger data-testid="admin-edit-role"><SelectValue /></SelectTrigger>
          <SelectContent><RoleOptions /></SelectContent>
        </Select>
      </div>
      <button onClick={save} data-testid="admin-edit-save" className="w-full bg-blue-800 hover:bg-blue-900 text-white font-semibold py-2.5 rounded-lg">Simpan</button>
    </div>
  );
};

const PinReset = ({ member }) => {
  const [pin, setPin] = useState("");
  const reset = async () => {
    try {
      await api.put(`/admin/members/${member.user_id}/pin`, { pin });
      toast.success("PIN diatur ulang. Anggota wajib mengganti PIN saat login.");
      setPin("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-600">Atur PIN awal baru (juga membuka kunci PIN). Anggota wajib menggantinya saat login berikutnya.</p>
      <PinInput value={pin} onChange={setPin} autoFocus={false} testid="admin-reset-pin-input" />
      <button onClick={reset} disabled={pin.length !== 6} data-testid="admin-reset-pin-submit" className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg">Atur Ulang PIN</button>
    </div>
  );
};

const deleteMember = async (member, onDone) => {
  if (!window.confirm(`Hapus akun ${member.name} (${member.member_no})? Riwayat transaksi tetap tersimpan.`)) return;
  try {
    await api.delete(`/admin/members/${member.user_id}`);
    toast.success("Anggota dihapus");
    onDone();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const DeleteMember = ({ member, onDone }) => {
  const del = () => deleteMember(member, onDone);
  return (
    <button onClick={del} data-testid="admin-delete-member" className="w-full mt-3 flex items-center justify-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 font-semibold py-2.5 rounded-lg">
      <Trash2 className="h-4 w-4" /> Hapus Anggota
    </button>
  );
};

const AddMemberDialog = ({ onClose, onDone }) => {
  const [f, setF] = useState({ email: "", name: "", phone: "", nik: "", address: "", role: "member", pin: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/members", f);
      toast.success("Anggota ditambahkan. Berikan PIN awal kepada anggota.");
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-member-dialog">
        <DialogHeader><DialogTitle>Tambah Anggota / Staff</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="add-member-email" type="email" placeholder="Email Google" value={f.email} onChange={set("email")} />
          <Input data-testid="add-member-name" placeholder="Nama lengkap" value={f.name} onChange={set("name")} />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="add-member-phone" placeholder="No. HP" value={f.phone} onChange={set("phone")} />
            <Input data-testid="add-member-nik" placeholder="NIK" value={f.nik} onChange={set("nik")} />
          </div>
          <Textarea data-testid="add-member-address" placeholder="Alamat" value={f.address} onChange={set("address")} />
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="add-member-role"><SelectValue /></SelectTrigger>
            <SelectContent><RoleOptions /></SelectContent>
          </Select>
          <div>
            <label className="text-sm font-semibold">PIN awal (6 angka)</label>
            <div className="mt-2"><PinInput value={f.pin} onChange={(v) => setF({ ...f, pin: v })} autoFocus={false} testid="add-member-pin" /></div>
          </div>
          <button onClick={save} disabled={busy || f.pin.length !== 6} data-testid="add-member-submit" className="w-full bg-gradient-to-r from-emerald-600 to-green-500 hover:brightness-105 disabled:opacity-50 text-white font-semibold py-3 rounded-xl">Simpan Anggota</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MemberDialog = ({ member, tab = "simpanan", onClose, onChanged }) => {
  const { user } = useAuth();
  const { data, reload } = useApi(`/admin/members/${member.user_id}/savings`);
  const refresh = () => { reload(); onChanged(); };
  const manageable = member.user_id !== user.user_id && (user.role === "superadmin" || rank(user) > rank(member));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-member-dialog">
        <DialogHeader><DialogTitle>{member.name} • {member.member_no}</DialogTitle>{member.account_no && <div className="text-xs text-slate-500">No. Rekening <AccountNo value={member.account_no} className="text-slate-800" testid="admin-member-account" /></div>}</DialogHeader>
        <div className="flex items-center gap-4" data-testid="admin-member-photo-location">
          <UserPhoto user={member} className="h-20 w-16 rounded-xl text-xl" testid="admin-member-photo" />
          <div className="text-sm">
            <div className="font-semibold">Tikor Anggota</div>
            {member.location ? (
              <>
                <a href={mapsUrl(member.location)} target="_blank" rel="noreferrer" className="text-blue-800 underline" data-testid="admin-member-location-link">{fmtCoord(member.location)}</a>
                <div className="text-[11px] text-slate-500">Akurasi ±{Math.round(member.location.accuracy || 0)} m • {fmtDate(member.location.captured_at)}</div>
              </>
            ) : <div className="text-slate-500 text-xs">Belum tercatat</div>}
          </div>
        </div>
        {data && (
          <div className="grid grid-cols-4 gap-2 text-center">
            {["pokok", "wajib", "sukarela", "total"].map((k) => (
              <div key={k} className="bg-slate-50 rounded-lg p-2"><div className="text-[11px] capitalize text-slate-500">{k}</div><div className="text-xs font-bold" data-testid={`admin-member-balance-${k}`}>{rp(data.balances[k])}</div></div>
            ))}
          </div>
        )}
        <Tabs defaultValue={manageable ? tab : "simpanan"}>
          <TabsList className="w-full"><TabsTrigger value="simpanan" className="flex-1" data-testid="admin-tab-savings">Simpanan</TabsTrigger><TabsTrigger value="mutasi" className="flex-1" data-testid="admin-tab-mutasi">Mutasi</TabsTrigger>{manageable && <><TabsTrigger value="data" className="flex-1" data-testid="admin-tab-edit">Data</TabsTrigger><TabsTrigger value="pin" className="flex-1" data-testid="admin-tab-pin">PIN</TabsTrigger></>}</TabsList>
          <TabsContent value="simpanan"><SavingsForm member={member} onDone={refresh} /></TabsContent>
          <TabsContent value="mutasi">
            <div className="max-h-64 overflow-y-auto divide-y">
              {data?.transactions.length === 0 && <p className="text-sm text-slate-500 py-3">Belum ada mutasi.</p>}
              {data?.transactions.map((t) => (
                <div key={t.tx_id} className="flex justify-between py-2 text-xs">
                  <span>{fmtDate(t.created_at)} • {t.description}</span>
                  <b className={t.direction === "in" ? "text-emerald-600" : "text-red-600"}>{t.direction === "in" ? "+" : "-"}{rp(t.amount)}</b>
                </div>
              ))}
            </div>
          </TabsContent>
          {manageable && (
            <>
              <TabsContent value="data">
                <EditForm member={member} onDone={() => { onChanged(); onClose(); }} />
                {user.role === "superadmin" && <DeleteMember member={member} onDone={() => { onChanged(); onClose(); }} />}
              </TabsContent>
              <TabsContent value="pin"><PinReset member={member} /></TabsContent>
            </>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const ROLE_STYLE = {
  superadmin: "bg-gradient-to-r from-rose-600 to-orange-500 text-white",
  admin: "bg-gradient-to-r from-blue-600 to-sky-500 text-white",
  staff: "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white",
  member: "bg-emerald-50 text-emerald-700 border border-emerald-200",
};

export default function AdminMembers() {
  const { data, reload } = useApi("/admin/members");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const { user } = useAuth();
  const isSuper = user.role === "superadmin";
  const [adding, setAdding] = useState(false);
  const qd = q.replace(/\D/g, "");
  const list = (data || []).filter((m) => `${m.name} ${m.member_no} ${m.email}`.toLowerCase().includes(q.toLowerCase()) || (qd && (m.account_no || "").includes(qd)));
  const total = (data || []).reduce((s, m) => s + m.balances.total, 0);
  return (
    <div>
      <AdminTitle icon={Users} title="Anggota" sub="Kelola data anggota, PIN, serta setoran & penarikan simpanan">
        <button onClick={() => setAdding(true)} data-testid="add-member-button" className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-green-500 text-white font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-emerald-500/30 hover:brightness-105">
          <Plus className="h-4 w-4" /> Tambah Anggota
        </button>
      </AdminTitle>
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <GradientStat tone="blue" icon={Users} label="Total Akun" value={data?.length ?? "-"} testid="members-stat-total" />
        <GradientStat tone="green" icon={PiggyBank} label="Total Simpanan" value={rp(total)} testid="members-stat-savings" />
        <GradientStat tone="purple" icon={MapPin} label="Tikor Tercatat" value={(data || []).filter((m) => m.location).length} testid="members-stat-location" />
      </div>
      <div className="relative mb-4">
        <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
        <Input data-testid="admin-member-search" className="pl-9 bg-white h-10" placeholder="Cari nama / no. anggota / no. rekening / email" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="admin-members-table">
          <thead className="bg-gradient-to-r from-slate-50 to-blue-50 text-left text-xs font-mono uppercase tracking-wide text-slate-600">
            <tr><th className="p-3">No. Anggota / Rekening</th><th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Peran</th><th className="p-3">Status</th><th className="p-3">Tikor</th><th className="p-3 text-right">Total Simpanan</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.user_id} className="border-t border-slate-100 hover:bg-blue-50/40 transition-colors">
                <td className="p-3"><span className="font-semibold text-blue-700">{m.member_no}</span><div className="text-[11px] font-mono text-slate-500 whitespace-nowrap" data-testid={`admin-account-${m.member_no}`}>{fmtAcc(m.account_no)}</div></td>
                <td className="p-3"><div className="flex items-center gap-2"><UserPhoto user={m} className="h-8 w-8 rounded-full text-[10px]" />{m.name}</div></td>
                <td className="p-3 text-slate-500">{m.email}</td>
                <td className="p-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${ROLE_STYLE[m.role]}`} data-testid={`role-${m.member_no}`}>{ROLE_LABEL[m.role]}</span></td>
                <td className="p-3"><StatusBadge status={m.status} /></td>
                <td className="p-3 text-xs">{m.location ? <a href={mapsUrl(m.location)} target="_blank" rel="noreferrer" className="text-blue-800 underline" data-testid={`admin-location-${m.member_no}`}>{fmtCoord(m.location)}</a> : <span className="text-slate-400">-</span>}</td>
                <td className="p-3 text-right font-semibold text-emerald-600">{rp(m.balances.total)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {isSuper && m.user_id !== user.user_id && (
                      <>
                        <button onClick={() => setSel({ m, tab: "data" })} title="Edit data & email" data-testid={`quick-edit-${m.member_no}`} className="h-8 w-8 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 flex items-center justify-center"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setSel({ m, tab: "pin" })} title="Ganti PIN" data-testid={`quick-pin-${m.member_no}`} className="h-8 w-8 rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 flex items-center justify-center"><KeyRound className="h-4 w-4" /></button>
                        <button onClick={() => deleteMember(m, reload)} title="Hapus anggota" data-testid={`quick-delete-${m.member_no}`} className="h-8 w-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                    <button onClick={() => setSel({ m, tab: "simpanan" })} data-testid={`admin-manage-member-${m.member_no}`} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">Kelola</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && <MemberDialog key={`${sel.m.user_id}-${sel.tab}`} member={sel.m} tab={sel.tab} onClose={() => setSel(null)} onChanged={reload} />}
      {adding && <AddMemberDialog onClose={() => setAdding(false)} onDone={reload} />}
    </div>
  );
}
